/**
 * Running Total + Savings Widget
 */
class RunningTotalWidget extends HTMLElement {
  constructor() {
    super();
    this.elements = {};
    this.observers = [];
  }

  /* ---------------- Lifecycle ---------------- */

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    // Disconnect all observers on removal
    this.observers.forEach((obs) => obs.disconnect());
    this.observers = [];
  }

  /* ---------------- Initialization ---------------- */

  init() {
    try {
      this.cacheElements();

      if (!this.elements.quantityInput || !this.elements.totalAmount) return;

      this.registerEventListeners();
      this.observePriceChanges();
      this.updateDisplay();
    } catch (err) {
      console.warn("RunningTotalWidget init error:", err);
    }
  }

  cacheElements() {
    this.elements = {
      quantityInput: document.querySelector(".product-form-quantity .quantity-input"),
      totalAmount: this.querySelector("[data-total-amount]"),
      priceEachAmount: this.querySelector("[data-price-each-amount]"),
      totalLabel: this.querySelector(".total-label"),
      savingsDisplay: this.querySelector("[data-savings-display]"),
    };
  }

  /* ---------------- Event Handling ---------------- */

  registerEventListeners() {
    const { quantityInput } = this.elements;
    const handleQuantityChange = this.handleQuantityChange.bind(this);

    // Direct input events
    ["input", "change", "keyup", "paste"].forEach((evt) =>
      quantityInput.addEventListener(evt, handleQuantityChange)
    );

    // Quantity button clicks
    document.addEventListener("click", (e) => {
      if (e.target.matches(".quantity-button, .quantity__button, [data-quantity-button]")) {
        setTimeout(handleQuantityChange, 50);
      }
    });

    // Custom app event
    document.addEventListener("quantity:update", handleQuantityChange);
  }

  handleQuantityChange() {
    this.updateDisplay();
  }

  handlePriceChange() {
    this.updateDisplay();
  }

  /* ---------------- Price Helpers ---------------- */

  getCurrentPrice() {
    // Check for volume discount price first
    const volumePriceElement = document.querySelector(".product-info-wrapper .volume-discount-price .price-item--sale");
    const isVolumePriceVisible = volumePriceElement &&
      document.querySelector(".product-info-wrapper .volume-discount-price")?.style.display !== 'none';

    if (isVolumePriceVisible) {
      return this.cleanPrice(volumePriceElement);
    }

    // Check if customized price is hidden
    const customizedPriceElement = document.querySelector(".product-info-wrapper .samitaWS-customized-price");
    const isCustomizedPriceHidden = customizedPriceElement && customizedPriceElement.style.display === 'none';

    // If customized price is hidden, use the original price instead
    if (isCustomizedPriceHidden) {
      return this.getBasePrice();
    }

    // Otherwise, use the normal priority order
    return (
      this.cleanPrice(customizedPriceElement) ||
      this.cleanPrice(document.querySelector(".product-info-wrapper .price-wrapper .price-item--regular")) ||
      this.cleanPrice(document.querySelector(".product-info-wrapper [samitaws-product-price]"))
    );
  }

  getBasePrice() {
    // Check for volume discount strikethrough price first
    const volumeOriginalPrice = document.querySelector(".product-info-wrapper .volume-discount-price s.price-item--regular");
    const isVolumePriceVisible = volumeOriginalPrice &&
      document.querySelector(".product-info-wrapper .volume-discount-price")?.style.display !== 'none';

    if (isVolumePriceVisible) {
      return this.cleanPrice(volumeOriginalPrice);
    }

    return (
      this.cleanPrice(document.querySelector(".product-info-wrapper .samitaWS-customized-original-price")) ||
      this.cleanPrice(document.querySelector(".product-info-wrapper .price-wrapper .price-item--regular")) ||
      this.cleanPrice(document.querySelector(".product-info-wrapper [samitaws-product-price]"))
    );
  }

  cleanPrice(element) {
    return element ? parseFloat(element.textContent.replace(/[^\d.]/g, "")) * 100 : null;
  }

  /* ---------------- UI Updates ---------------- */

  updateDisplay() {
    const { quantityInput, priceEachAmount, totalAmount, totalLabel } = this.elements;

    const quantity = parseInt(quantityInput?.value, 10) || 1;
    const currentPrice = this.getCurrentPrice();
    const basePrice = this.getBasePrice();

    if (!currentPrice || !basePrice) return;

    const totalCurrent = currentPrice * quantity;
    const totalBase = basePrice * quantity;
    const amountSaved = totalBase - totalCurrent;

    if (priceEachAmount) {
      priceEachAmount.textContent = this.formatMoney(currentPrice);
    }

    // Only show "You save" when there is an actual discount
    const labelText = totalLabel?.textContent?.trim() || "Total:";
    const savingsText = amountSaved > 0 ? ` (You save ${this.formatMoney(amountSaved)})` : "";
    totalAmount.innerHTML = `${labelText} <span style="font-weight:800;">${this.formatMoney(totalCurrent)}</span>${savingsText}`;

    if (totalLabel) totalLabel.style.display = "none";
  }

  formatMoney(cents) {
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(cents / 100);
  }

  /* ---------------- Observers ---------------- */

  observePriceChanges() {
    const selectors = [".price-wrapper"];

    selectors.forEach((selector) => {
      const el = document.querySelector(selector);
      if (!el) return;

      const observer = new MutationObserver(() => this.handlePriceChange());
      observer.observe(el, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['style'] });
      this.observers.push(observer);
    });
  }
}

customElements.define("running-total-widget", RunningTotalWidget);
